import { PageHeader } from '@/components/layout/PageHeader';
import { PageShell } from '@/components/layout/PageShell';
import { Button } from '@/components/ui/button';
import { ApiError, api } from '@/lib/api';
import type { HttpEndpoint } from '@innermate/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { Cable, Plus, Trash2 } from 'lucide-react';

const AUTH_LABEL: Record<HttpEndpoint['authType'], string> = {
  none: '없음',
  bearer_static: 'Bearer 고정',
  basic: 'Basic',
  login_form: 'Login 자동',
};

export function AdminHttpEndpointsList() {
  const navigate = useNavigate();
  const qc = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'http-endpoints'],
    queryFn: () => api.adminHttpEndpointList(),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.adminHttpEndpointDelete(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'http-endpoints'] }),
  });

  const items = data?.endpoints ?? [];

  return (
    <PageShell
      header={
        <PageHeader
          title="사내 API Endpoint"
          count={items.length}
          description="Skill 이 호출할 수 있는 외부/사내 REST API 를 등록합니다. 인증은 서버에서 자동 주입됩니다."
          actions={
            <Button
              onClick={() => navigate({ to: '/admin/http-endpoints/$id', params: { id: 'new' } })}
            >
              <Plus className="size-4" />
              Endpoint 추가
            </Button>
          }
        />
      }
    >
      {isLoading ? (
        <p className="text-sm text-foreground-muted">불러오는 중…</p>
      ) : error ? (
        <p className="text-sm text-destructive">
          {error instanceof ApiError ? error.message : 'Endpoint 목록을 불러오지 못했습니다.'}
        </p>
      ) : items.length === 0 ? (
        <EmptyState
          onCreate={() => navigate({ to: '/admin/http-endpoints/$id', params: { id: 'new' } })}
        />
      ) : (
        <div className="rounded-2xl border border-border bg-surface overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-surface-muted/40 text-[11px] uppercase tracking-wider text-foreground-muted">
              <tr>
                <th className="px-4 py-2.5 text-left font-medium">이름</th>
                <th className="px-4 py-2.5 text-left font-medium">Base URL</th>
                <th className="px-4 py-2.5 text-left font-medium">인증</th>
                <th className="px-4 py-2.5 text-left font-medium">메서드</th>
                <th className="px-4 py-2.5 text-left font-medium">쿠키</th>
                <th className="px-4 py-2.5 text-right font-medium">액션</th>
              </tr>
            </thead>
            <tbody>
              {items.map((ep) => (
                // biome-ignore lint/a11y/useKeyWithClickEvents: 행 전체 클릭으로 상세 — 키보드 사용자는 행 안의 액션 버튼으로 접근
                <tr
                  key={ep.id}
                  className="border-t border-border hover:bg-surface-muted/30 cursor-pointer"
                  onClick={() =>
                    navigate({ to: '/admin/http-endpoints/$id', params: { id: ep.id } })
                  }
                >
                  <td className="px-4 py-3">
                    <div className="font-medium">{ep.name}</div>
                    {ep.description ? (
                      <div className="mt-0.5 text-xs text-foreground-muted truncate max-w-[320px]">
                        {ep.description}
                      </div>
                    ) : null}
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-foreground-muted">
                    {ep.baseUrl}
                  </td>
                  <td className="px-4 py-3 text-xs">{AUTH_LABEL[ep.authType]}</td>
                  <td className="px-4 py-3 font-mono text-xs text-foreground-muted">
                    {ep.allowedMethods.join(', ')}
                  </td>
                  <td className="px-4 py-3 text-xs text-foreground-muted">
                    {ep.cookieMode ? '사용' : '-'}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (window.confirm(`"${ep.name}" 을 삭제할까요?`)) {
                          deleteMutation.mutate(ep.id);
                        }
                      }}
                      aria-label="삭제"
                      className="size-8 inline-grid place-items-center rounded-md text-foreground-muted hover:bg-surface-muted hover:text-destructive"
                    >
                      <Trash2 className="size-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </PageShell>
  );
}

function EmptyState({ onCreate }: { onCreate: () => void }) {
  return (
    <div className="rounded-2xl border border-dashed border-border bg-surface/50 px-8 py-16 text-center">
      <Cable className="size-8 mx-auto text-foreground-muted" />
      <h3 className="mt-3 text-base font-medium">등록된 Endpoint 가 없습니다.</h3>
      <p className="mt-1 text-sm text-foreground-muted">
        Endpoint 를 등록하면 Skill 의 http.* 툴에서 선택해 LLM 이 호출할 수 있습니다.
      </p>
      <Button onClick={onCreate} className="mt-4">
        <Plus className="size-4" />
        Endpoint 추가
      </Button>
    </div>
  );
}
